import type { MastodonInstance, MastodonInstanceV2 } from './types';

export function getAuthData() {
  const domain = localStorage.getItem('domain');
  const access_token = localStorage.getItem('access_token');

  if (!domain || !access_token) {
    throw new Error('No authentication data found');
  }

  return { domain, access_token };
}

export function isV2Instance(
  data: MastodonInstance,
): data is MastodonInstanceV2 {
  return (
    'domain' in data &&
    'registrations' in data &&
    typeof data.registrations === 'object'
  );
}

export async function fetchInstanceInfo(
  domain: string,
): Promise<MastodonInstance> {
  try {
    const response = await fetch(`https://${domain}/api/v2/instance`);
    if (response.ok) {
      return (await response.json()) as MastodonInstance;
    }
  } catch (error) {
    console.warn(
      'Failed to fetch v2 instance info, falling back to v1:',
      error,
    );
  }

  const response = await fetch(`https://${domain}/api/v1/instance`);
  if (!response.ok) {
    throw new Error(`Failed to fetch instance info: ${response.status}`);
  }
  return (await response.json()) as MastodonInstance;
}

export function normalizeInstanceData(data: MastodonInstance) {
  if (isV2Instance(data)) {
    const access = data.configuration?.timeline_access;
    return {
      domain: data.domain,
      title: data.title,
      version: data.version,
      languages: data.languages,
      registrations_enabled: data.registrations.enabled,
      streaming_url: data.configuration?.urls?.streaming,
      status_page_url: data.configuration?.urls?.status,
      limited_federation: data.configuration?.limited_federation,
      contact_account: data.contact?.account,
      configuration: data.configuration,
      mastodon_api_version: data.api_versions?.mastodon,
      live_feeds: access?.live_feeds,
      topic_feeds: access?.hashtag_feeds,
    };
  } else {
    return {
      domain: data.uri,
      title: data.title,
      version: data.version,
      languages: data.languages,
      registrations_enabled: data.registrations,
      streaming_url: data.urls?.streaming_api,
      status_page_url: '',
      limited_federation: false,
      contact_account: data.contact_account,
      configuration: data.configuration,
      mastodon_api_version: undefined,
      live_feeds: undefined,
      topic_feeds: undefined,
    };
  }
}

export async function detectSearchApiVersion(
  domain: string,
  headers: Record<string, string>,
): Promise<2 | 1 | 0> {
  try {
    const v2Response = await fetch(
      `https://${domain}/api/v2/search?q=test&limit=1`,
      {
        method: 'GET',
        headers,
      },
    );
    if (v2Response.ok) {
      return 2;
    }
  } catch (error) {
    console.warn('Failed to check v2 search API:', error);
  }

  try {
    const v1Response = await fetch(
      `https://${domain}/api/v1/search?q=test&limit=1`,
      {
        method: 'GET',
        headers,
      },
    );
    if (v1Response.ok) {
      return 1;
    }
  } catch (error) {
    console.warn('Failed to check v1 search API:', error);
  }

  return 0;
}
